<template>
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
        @click.self="close">
        <div
            class="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col animate-fadeIn">
            <!-- Header -->
            <div
                class="px-6 py-4 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 class="text-xl font-bold text-slate-800">Guida API Key Gemini</h2>
                <button @click="close"
                    class="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-full hover:bg-slate-100">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Content -->
            <div class="p-6 text-slate-600 space-y-6 text-sm leading-relaxed">

                <p class="font-medium text-slate-700">
                    Ottenere una API Key per Gemini è il primo passo per integrare l'intelligenza artificiale nei tuoi
                    progetti (app, script Python, bot). La buona notizia è che, grazie a Google AI Studio, la procedura
                    è <strong>gratuita</strong> e richiede meno di 2 minuti.
                </p>

                <div class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5"
                        fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                        <p class="text-red-800 font-bold uppercase tracking-wide text-xs mb-1">Avvertenza Importante</p>
                        <p class="text-red-700 font-medium">La chiave può essere generata <span
                                class="underline decoration-2 underline-offset-2 font-bold">SOLO da studenti
                                maggiorenni</span> (18+).</p>
                    </div>
                </div>

                <div class="bg-blue-50 border border-blue-100 rounded-lg p-4 text-blue-800">
                    <strong>Ecco la guida completa aggiornata al 2026.</strong>
                </div>

                <div class="space-y-4">
                    <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <span
                            class="bg-slate-800 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
                        Accedi a Google AI Studio
                    </h3>
                    <p>
                        Il punto di partenza non è il sito di Gemini che usi per chattare, ma la piattaforma dedicata
                        agli sviluppatori.
                    </p>
                    <ul class="list-disc pl-5 space-y-1 ml-2">
                        <li>Vai su <a href="https://aistudio.google.com" target="_blank"
                                class="text-blue-600 hover:underline font-medium">aistudio.google.com</a>.</li>
                        <li>Accedi con il tuo account Google (quello che usi per Gmail o l'università).</li>
                        <li>Se è la prima volta che entri, ti verrà chiesto di accettare i Termini di Servizio. Spunta
                            le caselle necessarie e clicca su <strong>Continue</strong>.</li>
                    </ul>
                </div>

                <div class="space-y-4">
                    <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <span
                            class="bg-slate-800 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                        Crea la tua API Key
                    </h3>
                    <p>Una volta dentro la dashboard, segui questi passaggi:</p>
                    <ul class="list-disc pl-5 space-y-1 ml-2">
                        <li>Nel menu a sinistra, clicca sul pulsante con l'icona della chiave: <strong>"Get API
                                key"</strong>.</li>
                        <li>Clicca sul tasto azzurro <strong>"Create API key"</strong>.</li>
                        <li>Ti verranno date due opzioni:
                            <ul class="list-circle pl-5 mt-2 space-y-1 text-slate-500">
                                <li><strong>Create API key in new project:</strong> Consigliata per gli studenti. Google
                                    creerà automaticamente un "contenitore" (progetto) per la tua chiave.</li>
                                <li><strong>Create API key in existing project:</strong> Se hai già dei progetti su
                                    Google Cloud.</li>
                            </ul>
                        </li>
                        <li>Attendi qualche secondo: apparirà una finestra con la tua chiave (una stringa di lettere e
                            numeri).</li>
                    </ul>
                </div>

                <div class="space-y-4">
                    <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <span
                            class="bg-slate-800 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">3</span>
                        Salva la chiave in sicurezza
                    </h3>
                    <div class="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                        <p class="text-amber-800 text-xs font-bold uppercase mb-1">Importante</p>
                        <p>La chiave non deve essere condivisa pubblicamente (ad esempio su GitHub).</p>
                    </div>
                    <ul class="list-disc pl-5 space-y-1 ml-2">
                        <li>Clicca su <strong>Copy</strong> per copiarla.</li>
                        <li>Incollala in un file locale o in un gestore di password.</li>
                    </ul>
                    <p class="text-xs text-slate-500 italic">
                        Nota bene: Se perdi la chiave, non puoi visualizzarla di nuovo per intero per motivi di
                        sicurezza; dovrai crearne una nuova.
                    </p>
                </div>

                <div class="border rounded-lg overflow-hidden">
                    <div class="bg-slate-50 px-4 py-2 font-bold text-slate-700 border-b">Tabella dei Limiti (Piano Free)
                    </div>
                    <div class="p-4 bg-white">
                        <p class="mb-3 text-xs">Come studente, il piano gratuito è solitamente più che sufficiente per
                            test e piccoli progetti.</p>
                        <table class="w-full text-sm text-left">
                            <thead class="bg-slate-100 text-slate-600 font-bold uppercase text-xs">
                                <tr>
                                    <th class="px-3 py-2 rounded-tl-md">Caratteristica</th>
                                    <th class="px-3 py-2 rounded-tr-md">Limite Piano Gratuito (Free Tier)</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-slate-100">
                                <tr>
                                    <td class="px-3 py-2 font-medium text-slate-700">Costo</td>
                                    <td class="px-3 py-2 text-green-600 font-bold">$0 (Gratis)</td>
                                </tr>
                                <tr>
                                    <td class="px-3 py-2 font-medium text-slate-700">Richieste al minuto (RPM)</td>
                                    <td class="px-3 py-2">15 (per Gemini 2.5 Flash)</td>
                                </tr>
                                <tr>
                                    <td class="px-3 py-2 font-medium text-slate-700">Richieste al giorno (RPD)</td>
                                    <td class="px-3 py-2">1.500</td>
                                </tr>
                                <tr>
                                    <td class="px-3 py-2 font-medium text-slate-700">Privacy dei dati</td>
                                    <td class="px-3 py-2 text-slate-500 text-xs">I dati potrebbero essere usati da
                                        Google per migliorare i modelli.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>

            <!-- Footer -->
            <div class="p-4 border-t border-slate-100 flex justify-end bg-slate-50 rounded-b-xl">
                <button @click="close"
                    class="px-6 py-2 bg-slate-800 text-white font-bold rounded-lg shadow hover:bg-slate-700 transition">Ho
                    capito</button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ApiKeyModal',
    props: {
        isOpen: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close'],
    methods: {
        close() {
            this.$emit('close');
        }
    }
}
</script>

<style scoped>
.list-circle {
    list-style-type: circle;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.animate-fadeIn {
    animation: fadeIn 0.2s ease-out;
}
</style>
